import { useMemo } from 'react';
import { Extension } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { foldAll, unfoldAll, foldable, foldEffect } from '@codemirror/language';

// Get indentation level of a line (number of leading spaces/tabs)
function getIndentLevel(line: string, tabSize: number = 2): number {
  let indent = 0;
  for (const char of line) {
    if (char === ' ') indent++;
    else if (char === '\t') indent += tabSize;
    else break;
  }
  return Math.floor(indent / tabSize);
}

// Fold all regions at a specific indent level
function foldAtLevel(view: EditorView, level: number): boolean {
  const { state } = view;
  const effects: ReturnType<typeof foldEffect.of>[] = [];

  // Iterate through all lines
  for (let i = 1; i <= state.doc.lines; i++) {
    const line = state.doc.line(i);
    const lineText = line.text;

    // Skip empty lines
    if (lineText.trim() === '') continue;

    const lineIndent = getIndentLevel(lineText);

    // If line is at the target level, try to fold it
    if (lineIndent === level) {
      const foldRange = foldable(state, line.from, line.to);
      if (foldRange) {
        effects.push(foldEffect.of({ from: foldRange.from, to: foldRange.to }));
      }
    }
  }

  if (effects.length > 0) {
    view.dispatch({ effects });
    return true;
  }

  return false;
}

// Command to fold at level 1
const foldLevel1 = (view: EditorView) => foldAtLevel(view, 0);
const foldLevel2 = (view: EditorView) => foldAtLevel(view, 1);
const foldLevel3 = (view: EditorView) => foldAtLevel(view, 2);
const foldLevel4 = (view: EditorView) => foldAtLevel(view, 3);
const foldLevel5 = (view: EditorView) => foldAtLevel(view, 4);

// Commands exposed for command palette
export const foldByIndentCommands = [
  { name: 'Fold Level 1', fn: foldLevel1 },
  { name: 'Fold Level 2', fn: foldLevel2 },
  { name: 'Fold Level 3', fn: foldLevel3 },
  { name: 'Fold Level 4', fn: foldLevel4 },
  { name: 'Fold Level 5', fn: foldLevel5 },
  { name: 'Fold All', fn: foldAll },
  { name: 'Unfold All', fn: unfoldAll },
];

export function useFoldByIndent() {
  const foldByIndentExtension = useMemo<Extension>(() => {
    return keymap.of([
      // Cmd+K Cmd+1 to fold level 1, etc.
      {
        key: 'Mod-k Mod-1',
        run: foldLevel1,
      },
      {
        key: 'Mod-k Mod-2',
        run: foldLevel2,
      },
      {
        key: 'Mod-k Mod-3',
        run: foldLevel3,
      },
      {
        key: 'Mod-k Mod-4',
        run: foldLevel4,
      },
      {
        key: 'Mod-k Mod-5',
        run: foldLevel5,
      },
      // Cmd+K Cmd+0 to fold all
      {
        key: 'Mod-k Mod-0',
        run: foldAll,
      },
      // Cmd+K Cmd+J to unfold all
      {
        key: 'Mod-k Mod-j',
        run: unfoldAll,
      },
    ]);
  }, []);

  return { foldByIndentExtension, foldByIndentCommands };
}
